import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox, getIsMeter } from "reduxModules/ducks/ui";
import { distanceToString } from "utilities/format";
import { LineDimensionProps, ForeignObjectProps } from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI LineDimension component that replaces the styled-components LineDimension component
 * 
 * This component provides a Material UI equivalent of the original LineDimension component
 * with similar props and functionality.
 * 
 * @returns {JSX.Element} MUI LineDimension component
 */
const MuiLineDimension: React.FC<LineDimensionProps> = (props) => {
  const { bbox, zoom } = useSelector(getDrawingBox);
  const isMeter = useSelector(getIsMeter);
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const { length, width } = useSelector((state: RootState) => 
    ((!zoom && state.container) || state.container.areas[activeTab]).dimensions
  ); 
  
  const lengthStr = distanceToString(length, isMeter);
  const widthStr = distanceToString(width, isMeter);
  
  const area = props.children;
  const visible = useSelector((state: RootState) => 
    getConditionResult(state, {...props.show, area: area.id})
  );

  return (
    visible ? (
      <Fragment>
        {[lengthStr, widthStr].map((dim, index) => {
          const isHorizontal = index === 0;
          // Only show vertical dimension (on the right side), hide horizontal dimension (on bottom)
          if (isHorizontal) return null;
          
          return (
            <ForeignObject 
              key={`dim-${index}`} 
              x={isHorizontal ? bbox.cx : bbox.x1}
              y={isHorizontal ? bbox.y1 : bbox.cy}
              width={140}
              height={26}
              transform={`rotate(${isHorizontal ? 0 : 90}) translate(-70,-14)`}
              transformOrigin={`${isHorizontal ? bbox.cx : bbox.x1}px ${isHorizontal ? bbox.y1 : bbox.cy}px`}
            >
              <DimWrapper>
                <DimInner>{dim}</DimInner>
              </DimWrapper>
            </ForeignObject>
          );
        })}
      </Fragment>
    ) : null
  );
};

// Styled components using MUI styled API
const ForeignObject = styled('foreignObject')<ForeignObjectProps>(() => ({
  // No additional styling needed as we're using inline props
}));

const DimWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const DimInner = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  alignItems: 'middle',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  background: '#fff',
  fontFamily: 'cursive',
  fontStyle: 'italic',
  userSelect: 'none',
  color: '#007bff',
}));

export default MuiLineDimension;
